/**
 * Active alerts for one device or one area, from the shared alert provider.
 *
 * `useNotifications()` (src/contexts/NotificationContext.jsx) holds the campus's
 * live alerts, pushed over AppSync. Exactly one of its two arrays is ever
 * populated — `activeCloudAlarms` on a 5.0 campus, `activeAlarms` on 4.0 — and
 * the two use almost entirely different field names. This module maps both onto
 * one shape so nothing downstream has to know which campus it is on.
 *
 * **Membership in those arrays is the definition of "active".** The provider drops
 * a row only when the backend re-sends it with an empty event timestamp, which is
 * its "this alert is finished" signal. So anything still present is a live alarm
 * session, whether or not the device has cleared yet.
 *
 * `status` is read as delivered, never re-derived. The provider computes it
 * (deriveCloudAlarmStatus / its 4.0 twin in AlertNotificationProvider.jsx) but
 * both helpers are module-private, and a second implementation would drift from
 * the one the alerts pages show.
 */

import { DateTime } from 'luxon';

/**
 * The onsite-server-offline alert (5.0 only, `event_category_id` 16).
 *
 * It arrives on the same subscription as every other alert but it is not a device
 * alarm, and it does not behave like one anywhere in this module:
 *
 *  - **Its `device_id` is a `palcare_device_id`**, not a `core_devices.device_id`.
 *    Those are separate id spaces that both start at low integers, so a server
 *    with palcare_device_id 57 and a pendant with device_id 57 are unrelated rows
 *    that would collide in any Map keyed on `deviceId`. Server-offline alerts are
 *    therefore excluded from device matching and device grouping — see
 *    `alertsForDevice` and `alertsByDevice`.
 *  - **It links somewhere else**: `/community/server_devices/:palcareDeviceId`
 *    rather than a device profile.
 *
 * A string because `event_type_id` arrives as a number on one path and a string on
 * the other, exactly like every other id in here.
 */
export const SERVER_OFFLINE_TYPE_ID = '41';

/** Same string both shapes can be compared through. */
const asKey = (value) => (value == null ? '' : String(value).trim());

/**
 * An id string, or '' when there is no real id.
 *
 * Both payloads use 0 to mean "not set" rather than omitting the field, and `asKey`
 * would turn that into the string "0" — truthy, and enough to build a link to a
 * non-existent area. Matches the `row.near_area_id ? link : text` guard
 * CloudEmergencyTable.jsx already applies.
 */
const idOrEmpty = (value) => {
  const str = asKey(value);
  return str && str !== '0' ? str : '';
};

/**
 * Epoch ms for a UTC timestamp, or null when absent/unparseable.
 *
 * `{ zone: 'utc' }` is not optional: both shapes deliver a naive string with no
 * zone marker, and without it luxon reads it in the browser's zone — which would
 * put every duration out by the viewer's offset.
 *
 * Note this is a different job from `convertIsoUTCToLocal`, which deliberately
 * parses zonelessly and does pure offset arithmetic because it only ever formats.
 * Durations need a real instant, so they cannot share that path.
 */
export function utcToMs(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  let dt = DateTime.fromSQL(raw, { zone: 'utc' });
  if (!dt.isValid) dt = DateTime.fromISO(raw, { zone: 'utc' });
  return dt.isValid ? dt.toMillis() : null;
}

/**
 * Elapsed time as a clock: `mm:ss` under an hour, `h:mm:ss` beyond.
 *
 * Monospace at the call site, because proportional digits change width as they
 * tick and the whole field jitters once a second.
 */
export function formatDurationClock(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const hours = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(mins)}:${pad(secs)}` : `${pad(mins)}:${pad(secs)}`;
}

/**
 * Short span for prose, e.g. the response time in "accepted after 38s".
 *
 * Drops to one unit past a minute — "1h 5m" not "1h 5m 12s". At that scale the
 * seconds are noise, and the point of the field is how long staff took.
 */
export function formatSpanShort(ms) {
  const total = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  if (total < 60) return `${total}s`;
  const mins = Math.floor(total / 60);
  if (mins < 60) {
    const secs = total % 60;
    return secs ? `${mins}m ${secs}s` : `${mins}m`;
  }
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hours}h ${rem}m` : `${hours}h`;
}

/**
 * Resident display name from either shape.
 *
 * Cloud delivers a normalized `residents` array; 4.0 delivers `ResidentsObject`,
 * parsed by the provider from a JSON string. Either can be absent — an area
 * device has no resident at all — so this returns '' rather than a placeholder
 * and lets the caller decide how to render nothing.
 */
function residentList(row) {
  if (Array.isArray(row?.residents)) return row.residents;
  return Array.isArray(row?.ResidentsObject) ? row.ResidentsObject : [];
}

function residentNames(row) {
  return residentList(row)
    /* Three key styles, because the two payloads disagree and 4.0 itself varies.
       Cloud is normalized by the provider to resident_name. 4.0's raw records use
       camelCase firstName/lastName, with fName/lName as an older variant — the same
       fallback chain EmergencyTable.jsx renderResidents uses. Reading only the
       resident_* keys left every 4.0 name blank. */
    .map((r) => r?.resident_name
      || `${r?.resident_first_name || r?.firstName || r?.fName || ''} `
        + `${r?.resident_last_name || r?.lastName || r?.lName || ''}`.trim())
    .map((name) => name.trim())
    .filter(Boolean)
    .join(', ');
}

/**
 * Every resident id on the alert, as strings.
 *
 * An alert can name more than one resident — a shared room, a device covering two
 * people — so this is a list, not a single id, and the resident filter tests
 * membership. Both shapes carry ids: cloud normalizes `residents[]` to
 * {resident_id, resident_name}, and 4.0's parsed ResidentsObject entries carry
 * resident_id too (EmergencyTable.jsx links each one).
 */
function residentIds(row) {
  return residentList(row)
    .map((r) => idOrEmpty(r?.resident_id))
    .filter(Boolean);
}

/**
 * One alert row, either shape, onto a single internal shape.
 *
 * Returns **null** for anything without a recognisable id. A shape change
 * upstream then degrades to "no alerts shown" instead of throwing part-way
 * through a render, which on this page would blank the whole device profile.
 *
 * @param {object} row — a member of activeCloudAlarms or activeAlarms
 * @returns {{
 *   key: string, isCloud: boolean, eventId: string|null, deviceId: string,
 *   deviceName: string, manufactureId: string, deviceTypeId: string, serial: string,
 *   startedUtc: string, category: string, type: string, status: string,
 *   acceptedBy: string, acceptedUtc: string, clearedUtc: string,
 *   areaId: string, areaName: string, nearAreaName: string, nearAreaId: string,
 *   residentName: string, residentIds: Array<string>,
 * }|null}
 */
export function normalizeAlert(row) {
  if (!row || typeof row !== 'object') return null;

  const cloudKey = asKey(row.event_id);
  const legacyKey = asKey(row.AlarmId);
  if (!cloudKey && !legacyKey) return null;

  const isCloud = Boolean(cloudKey);

  return isCloud
    ? {
      key: cloudKey,
      isCloud: true,
      /* Numeric event id, so it can link to /profiles/event/:id. */
      eventId: cloudKey,
      deviceId: asKey(row.device_id),
      deviceName: row.device_name || '',
      /* Only used to build the device-profile link, so it stays a string. */
      manufactureId: asKey(row.manufacture_id),
      /* With manufactureId, lets the link builder skip Inovonics system types
         (repeaters etc.), whose profile page is empty. */
      deviceTypeId: asKey(row.device_type_id),
      /* Carried so callers can tell a device alarm from the server-offline alert,
         which shares this shape but not the device id space — see
         SERVER_OFFLINE_TYPE_ID. */
      eventTypeId: asKey(row.event_type_id),
      serial: asKey(row.manufacture_uuid),
      startedUtc: row.event_datetime_utc || '',
      category: row.event_category || '',
      type: row.event_type || '',
      status: row.status || '',
      acceptedBy: row.accepted_display || row.accepted_username || '',
      acceptedUtc: row.accepted_datetime_utc || '',
      clearedUtc: row.ack_datetime_utc || '',
      areaId: asKey(row.area_id),
      areaName: row.area_name || '',
      nearAreaName: row.near_area_name || '',
      nearAreaId: idOrEmpty(row.near_area_id),
      residentName: residentNames(row),
      residentIds: residentIds(row),
    }
    : {
      key: legacyKey,
      isCloud: false,
      /* 4.0's AlarmId is not the numeric event id, so there is nothing to link
         to — the caller must not build an event URL from it. */
      eventId: null,
      deviceId: asKey(row.DeviceId),
      /* DeviceUse is 4.0's device label — it is what EmergencyTable.jsx shows in
         its own "Device / Serial" column, so this matches the alerts page rather
         than inventing a second convention. */
      deviceName: row.DeviceUse || '',
      /* 4.0 sends no manufacturer, and every 4.0 device is Inovonics. Stated here
         so the link builder does not have to special-case the empty string. */
      manufactureId: '1',
      /* 4.0 sends no device type either; empty means "not a known system type". */
      deviceTypeId: '',
      /* 4.0 has no event_type_id, and the server-offline alert is 5.0-only, so
         there is nothing to map. Stated rather than left undefined so
         isServerOfflineAlert does not have to guard for a missing key. */
      eventTypeId: '',
      serial: asKey(row.SerialNumber || row.MacAddr),
      startedUtc: row.EventDateTime || '',
      category: row.AlertType || '',
      type: row.AlertSubType || '',
      status: row.Status || '',
      acceptedBy: row.AcceptUsername || '',
      acceptedUtc: row.AcceptDateTime || '',
      clearedUtc: row.AckDateTime || '',
      areaId: asKey(row.LocationId),
      areaName: row.LocationName || '',
      /* NearName, never Near. `Near` is a raw JSON array of records; the provider
         parses it and joins the `name` of each into NearName (see
         AlertNotificationProvider.jsx). Reading Near here would print the JSON.
         Either way there is no near-area *id*, which is why area filtering cannot
         offer a near-area option (see alertsForArea). */
      nearAreaName: row.NearName || '',
      /* No id on 4.0, so the near-area can never be a link there. The raw `Near`
         records are written by the on-prem server and only their `name` is ever
         read — by the provider, by Get_Alerts_By_Campus.py, and by
         EmergencyTable.jsx, which renders Near as plain text for exactly this
         reason. If those records ever gain an area id, map it here and the link
         appears with no other change. */
      nearAreaId: '',
      residentName: residentNames(row),
      residentIds: residentIds(row),
    };
}

/**
 * Is this normalized alert the onsite-server-offline alert?
 *
 * @param {object|null} alert — a normalizeAlert result
 * @returns {boolean}
 */
export function isServerOfflineAlert(alert) {
  return Boolean(alert) && alert.eventTypeId === SERVER_OFFLINE_TYPE_ID;
}

/**
 * Every active onsite-server-offline alert, oldest first.
 *
 * The data source for the global offline banner. Both arrays are accepted for the
 * same reason as everywhere else in here — the caller should not have to know
 * which stack it is on — even though only 5.0 can produce one.
 *
 * @param {Array<object>} cloudRows — activeCloudAlarms
 * @param {Array<object>} legacyRows — activeAlarms
 * @returns {Array<object>} normalized alerts
 */
export function serverOfflineAlerts(cloudRows, legacyRows) {
  /* SERVER_OFFLINE_TYPE_ID is the `target` here, not decoration: `matching`
     returns [] for a falsy target, so omitting it would make this silently empty
     forever — the banner would simply never appear and nothing would look
     broken. */
  return matching(cloudRows, legacyRows, isServerOfflineAlert, SERVER_OFFLINE_TYPE_ID);
}

/**
 * This device's active alerts, oldest first.
 *
 * Both arrays are accepted because only one is ever populated and the caller
 * should not have to work out which — passing both and letting this concatenate
 * is what keeps `is_cloud` out of the page.
 *
 * Device ids are compared as strings: the subscription delivers them as a number
 * on one path and a string on the other, so `===` on raw values silently matches
 * nothing.
 *
 * @param {Array<object>} cloudRows — activeCloudAlarms
 * @param {Array<object>} legacyRows — activeAlarms
 * @param {number|string} deviceId
 * @returns {Array<object>} normalized alerts
 */
export function alertsForDevice(cloudRows, legacyRows, deviceId) {
  /* Server-offline alerts are excluded: their device_id is a palcare_device_id
     from a different id space, so matching one here would put a server's alarm on
     an unrelated pendant's profile and gate that pendant's actions. See
     SERVER_OFFLINE_TYPE_ID. */
  return matching(
    cloudRows,
    legacyRows,
    (a) => !isServerOfflineAlert(a) && a.deviceId === asKey(deviceId),
    deviceId,
  );
}

/**
 * This area's active alerts, oldest first.
 *
 * Matches on the alert's own area (`area_id` / `LocationId`) — the area the
 * device or its resident is assigned to.
 *
 * **Near-area matches are deliberately excluded.** A wandering tag belonging to
 * another room can be detected at this one, and `near_area_id` is how the alerts
 * table links that, but 4.0 sends only a near-area *name* and no id — so offering
 * it would show extra alerts on 5.0 campuses and silently none on 4.0. Uniform
 * behaviour was chosen over the extra coverage.
 *
 * @param {Array<object>} cloudRows — activeCloudAlarms
 * @param {Array<object>} legacyRows — activeAlarms
 * @param {number|string} areaId
 * @returns {Array<object>} normalized alerts
 */
export function alertsForArea(cloudRows, legacyRows, areaId) {
  return matching(cloudRows, legacyRows, (a) => a.areaId === asKey(areaId), areaId);
}

/**
 * This resident's active alerts, oldest first.
 *
 * Matches when the alert names this resident among its residents — not on the
 * device's assigned area or resident. A resident with two pendants, or one sharing
 * a room, has every alert that names them appear here regardless of which device
 * raised it or where it was assigned.
 *
 * @param {Array<object>} cloudRows — activeCloudAlarms
 * @param {Array<object>} legacyRows — activeAlarms
 * @param {number|string} residentId
 * @returns {Array<object>} normalized alerts
 */
export function alertsForResident(cloudRows, legacyRows, residentId) {
  const target = asKey(residentId);
  return matching(cloudRows, legacyRows, (a) => a.residentIds.includes(target), residentId);
}

/**
 * Device ids with any active alert, as a Set of strings.
 *
 * Deliberately NOT scoped to an area or a device. Callers use this to block
 * actions that could swallow a device's clear signal — unassigning it, muting it —
 * and that risk belongs to the device wherever its alert happens to be filed. An
 * area-scoped set would miss a device sitting in this room whose alert carries a
 * different area_id, which is exactly the case the guard exists for.
 *
 * @param {Array<object>} cloudRows — activeCloudAlarms
 * @param {Array<object>} legacyRows — activeAlarms
 * @returns {Set<string>} device ids, as strings
 */
export function activeAlertDeviceIds(cloudRows, legacyRows) {
  return new Set(alertsByDevice(cloudRows, legacyRows).keys());
}

/**
 * The blocked-reason copy for a device action refused mid-alarm (remove, assign,
 * reassign), shown in BlockedReasonTip. One string so the list drawers and the
 * profile pages cannot drift apart in wording. It says *wait*, not "no permission",
 * because the block clears on its own. See docs/ACTIVE_ALARM_GATING.md.
 */
export const DEVICE_ALARM_BLOCKED_MSG =
  'This device has an active alarm. Acting on it now could stop the clear signal being processed. Wait until the alert is resolved.';

/**
 * The blocked-reason copy for a resident action refused mid-alarm (move out, delete,
 * relocate), shared by ResidentsPage and ResidentProfilePage. Edit is deliberately
 * not gated: renaming a resident does not touch the assignment the alarm runs on.
 */
export const RESIDENT_ALARM_BLOCKED_MSG = 'This resident has an active alarm. Wait until the alert is resolved.';

/**
 * Area ids with any active alert, as a Set of strings.
 *
 * The companion to activeAlertDeviceIds, for gating actions on the AREA rather
 * than on a device — deleting a room while something in it is alarming.
 *
 * Matches on the alert's own `area_id` (5.0) / `LocationId` (4.0) only. Near-area
 * matches are excluded, same as alertsForArea: 4.0 sends no near-area id, so
 * including them would block on a cloud campus and silently not on an on-prem
 * one. Both stacks file real portal area ids here — AlertsPage links LocationId
 * straight to /profiles/area/:id — so this works on 4.0 as well as 5.0.
 *
 * @param {Array<object>} cloudRows — activeCloudAlarms
 * @param {Array<object>} legacyRows — activeAlarms
 * @returns {Set<string>} area ids, as strings
 */
export function activeAlertAreaIds(cloudRows, legacyRows) {
  return new Set(alertsByArea(cloudRows, legacyRows).keys());
}

/**
 * Resident ids with any active alert, as a Set of strings.
 *
 * An alert can name more than one resident, so every id on the row is added.
 * Gates the resident-level actions on ResidentsPage — move out, delete, relocate —
 * where the risk is the same as the device one: those writes change or end the
 * assignment the alarm is running against.
 *
 * @param {Array<object>} cloudRows — activeCloudAlarms
 * @param {Array<object>} legacyRows — activeAlarms
 * @returns {Set<string>} resident ids, as strings
 */
export function activeAlertResidentIds(cloudRows, legacyRows) {
  return new Set(alertsByResident(cloudRows, legacyRows).keys());
}

/**
 * Active alerts grouped by area id — Map<areaIdString, alert[]>, each list oldest
 * first.
 *
 * For a list page that has to render per-row detail without running a filter per
 * row: one pass over the alerts instead of one pass per area. activeAlertAreaIds
 * is derived from this so the gate and the row indicator can never disagree about
 * which areas are alarming.
 *
 * Same area_id-only matching as activeAlertAreaIds — no near-area.
 *
 * @param {Array<object>} cloudRows — activeCloudAlarms
 * @param {Array<object>} legacyRows — activeAlarms
 * @returns {Map<string, Array<object>>} normalized alerts, keyed by area id
 */
export function alertsByArea(cloudRows, legacyRows) {
  return groupBy(cloudRows, legacyRows, (a) => (a.areaId ? [a.areaId] : []));
}

/**
 * Active alerts grouped by device id — Map<deviceIdString, alert[]>, oldest first.
 *
 * Feeds the row marker on DevicesPage. activeAlertDeviceIds is derived from this,
 * so this exclusion covers every device gate on every page at once.
 *
 * **Server-offline alerts are left out.** Their `device_id` is a
 * `palcare_device_id`, which shares no id space with `core_devices.device_id` —
 * both are small integers, so a server with palcare_device_id 57 would key into
 * bucket "57" and mark, and gate, whichever pendant happens to hold device_id 57.
 * See SERVER_OFFLINE_TYPE_ID.
 */
export function alertsByDevice(cloudRows, legacyRows) {
  return groupBy(
    cloudRows,
    legacyRows,
    (a) => (a.deviceId && !isServerOfflineAlert(a) ? [a.deviceId] : []),
  );
}

/**
 * Active alerts grouped by resident id — Map<residentIdString, alert[]>, oldest
 * first.
 *
 * An alert can name several residents, so the same alert lands in more than one
 * bucket. Feeds the row marker on ResidentsPage; activeAlertResidentIds is derived
 * from this.
 */
export function alertsByResident(cloudRows, legacyRows) {
  return groupBy(cloudRows, legacyRows, (a) => a.residentIds || []);
}

/**
 * Shared grouper. `keysOf` returns every bucket an alert belongs in — one for a
 * device or an area, possibly several for residents.
 *
 * One pass over the alerts rather than one filter per table row, which is the whole
 * point on a list page. Buckets are sorted individually rather than the input being
 * sorted up front, so a campus with a single alert does no sorting at all.
 */
function groupBy(cloudRows, legacyRows, keysOf) {
  const out = new Map();
  [...(cloudRows || []), ...(legacyRows || [])].forEach((row) => {
    const a = normalizeAlert(row);
    if (!a) return;
    keysOf(a).forEach((key) => {
      if (!key) return;
      const list = out.get(key);
      if (list) list.push(a);
      else out.set(key, [a]);
    });
  });
  /* Oldest first, matching the profile-page banners — the alert that has been
     waiting longest leads. */
  out.forEach((list) => list.sort((a, b) => startedMs(a) - startedMs(b)));
  return out;
}

/**
 * Shared body of the two filters: normalize, keep what `test` accepts, oldest
 * first. `target` is checked only to bail early on a missing id — without it an
 * empty target would match every row whose own id is also empty.
 */
function matching(cloudRows, legacyRows, test, target) {
  if (!asKey(target)) return [];

  return [...(cloudRows || []), ...(legacyRows || [])]
    .map(normalizeAlert)
    .filter((a) => a && test(a))
    /* Oldest first: the alert that has been waiting longest is the one to deal
       with, so it leads. An unparseable timestamp sorts to 0 rather than NaN,
       which would make the comparator inconsistent and the order arbitrary —
       note that puts an undated alert first, which is the safe direction for a
       list ordered by urgency. */
    .sort((a, b) => startedMs(a) - startedMs(b));
}

/** Epoch ms for sorting, 0 when absent or unparseable. */
function startedMs(alert) {
  return utcToMs(alert?.startedUtc) ?? 0;
}
